import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Pagination, Skeleton, Snackbar, Stack, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';

import axiosInstance from '../../axiosInstance';
import Fiche from '../Expert/Fiche';

const PAR_PAGE = 10;

/**
 * Les modèles de documents de l'organisation.
 *
 * Générer un modèle le remplit avec les données du dossier et dépose le
 * résultat dans ses pièces — ce n'est pas un téléchargement.
 */
function DocumentFil({ expertiseId, onSuccess }) {
  const [modeles, setModeles] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [chargement, setChargement] = useState(true);
  const [enCours, setEnCours] = useState(null);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await axiosInstance.get('/orgafiles/',
                                               { params: { page, limit: PAR_PAGE } });
      setModeles(data.results || data);
      setTotal(data.total || 0);
      setPages(data.total_pages
        || Math.max(1, Math.ceil((data.total || 0) / PAR_PAGE)));
      setErreur('');
    } catch {
      setErreur("Les modèles de documents n'ont pas pu être chargés.");
    } finally {
      setChargement(false);
    }
  }, [page]);

  useEffect(() => { charger(); }, [charger]);

  const generer = async (modele) => {
    setEnCours(modele.id);
    setErreur('');
    try {
      await axiosInstance.post(
        `/organization_file_fill/${expertiseId}/${modele.id}/`);
      setMessage(`« ${modele.title} » ajouté aux pièces du dossier`);
      charger();
      if (onSuccess) onSuccess();
    } catch {
      setErreur(`« ${modele.title} » n'a pas pu être généré.`);
    } finally {
      setEnCours(null);
    }
  };

  return (
    <Fiche
      titre="Modèles de documents"
      explication="Générer un modèle le remplit avec les données du dossier et dépose le résultat dans ses pièces."
      sx={{ p: 0 }}
    >
      {erreur && <Alert severity="error" sx={{ m: 2 }}
                        onClose={() => setErreur('')}>{erreur}</Alert>}

      {chargement && (
        <Stack spacing={1} sx={{ p: 2 }}>
          {[...Array(3)].map((_, i) => <Skeleton key={i} height={36} />)}
        </Stack>
      )}

      {!chargement && modeles.length === 0 && (
        <Typography variant="body2" color="text.secondary"
                    sx={{ py: 4, textAlign: 'center' }}>
          Aucun modèle de document pour votre organisation.
        </Typography>
      )}

      {!chargement && modeles.length > 0 && (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Modèle</TableCell>
                  <TableCell align="right">&nbsp;</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modeles.map((modele) => (
                  <TableRow key={modele.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {modele.title}
                      </Typography>
                      {modele.description && (
                        <Typography variant="caption" color="text.secondary">
                          {modele.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {/* Un bouton nommé plutôt qu'une icône : on ne devine
                          pas qu'un cadenas veut dire « générer ». */}
                      <Button size="small" variant="outlined"
                              onClick={() => generer(modele)}
                              disabled={enCours === modele.id}>
                        {enCours === modele.id ? 'Génération…' : 'Générer'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {pages > 1 && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}
                   alignItems="center" justifyContent="space-between"
                   sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {(page - 1) * PAR_PAGE + 1}–{Math.min(page * PAR_PAGE, total)}
                {' '}sur {total}
              </Typography>
              <Pagination count={pages} page={page}
                          onChange={(_, v) => setPage(v)} />
            </Stack>
          )}
        </>
      )}

      <Snackbar open={Boolean(message)} autoHideDuration={4000}
                onClose={() => setMessage('')} message={message}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
      <Box />
    </Fiche>
  );
}

export default DocumentFil;
